import { Add, ArrowLeft2 } from 'iconsax-react';
import { forwardRef, type PropsWithChildren, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import cn from '@/utils/merge-class-names';

export interface Props {
  navigateBack?: boolean | string | (() => void) | undefined;
  onNavigateBack?: () => void;
  className?: string;
  wrapperClassName?: string;
  elementBeforeNav?: ReactNode;
  elementAfterNav?: ReactNode;
  actions?: ReactNode;
  showHeader?: boolean;
  navigateAffinity?: 'start' | 'end';
}

const AppHeader = forwardRef<HTMLDivElement, PropsWithChildren<Props>>(
  (
    {
      children,
      className,
      wrapperClassName,
      elementBeforeNav,
      elementAfterNav,
      actions,
      navigateBack,
      onNavigateBack,
      showHeader = true,
      navigateAffinity = 'start',
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const { key: locationKey } = useLocation();

    const handleNavigateBack = async () => {
      if (locationKey === 'default') {
        return navigate('/', { replace: true });
      }

      switch (typeof navigateBack) {
        case 'boolean':
          navigateBack && (await navigate(-1));
          break;
        case 'string':
          await navigate(navigateBack, { replace: true });
          break;
        case 'function':
          navigateBack();
          break;
      }
      onNavigateBack?.();
    };

    return (
      <div
        className={cn('sticky top-0 z-30', wrapperClassName)}
        ref={ref}>
        {elementBeforeNav}

        <div
          className={cn(
            'navbar min-h-10 h-12 mode-embedded:h-auto mode-embedded:pb-1 desktop:h-18 w-full gap-4 px-5 bg-neutral-0 mode-embedded:pt-safe-offset-1 items-center group-[.modal-layout]:py-0 group-[.modal-layout]:px-4 mode-embedded:shadow',
            { hidden: !showHeader },
            className,
          )}>
          {navigateBack ? (
            navigateAffinity === 'start' ? (
              <button
                type='button'
                className='flex-none btn btn-square size-9 btn-ghost btn-sm'
                onClick={handleNavigateBack}>
                <ArrowLeft2 className='aspect-square size-6' />
              </button>
            ) : (
              <button
                type='button'
                className='flex-none order-last btn btn-square size-9 btn-ghost btn-sm'
                onClick={handleNavigateBack}>
                <Add className='aspect-square rotate-45 size-6' />
              </button>
            )
          ) : null}

          <div className='flex-1 basis-full min-w-0'>{children}</div>

          {actions ? (
            <div className='h-12 mode-embedded:h-9 mode-embedded:*:h-9 *:h-12 *:text-nowrap flex-none'>{actions}</div>
          ) : (
            <div className='h-12 mode-embedded:h-9 aspect-square invisible' />
          )}
        </div>

        {elementAfterNav}
      </div>
    );
  },
);

AppHeader.displayName = 'AppHeader';

export default AppHeader;
